import { List, Map } from 'immutable';

import { shapeScheduledListingsForApp } from '../utils/schedule/listings';
import { getTimeRangeEnd, getToday } from '../utils/schedule/time';
import { getDateKey, isAfter } from '../utils/time';
import { getPublishStatus } from '../utils/listings/statuses';
import { createController } from '../utils/controller';
import { getPagination } from '../utils/schedule/pagination';
import { shapeNumber } from '../utils/listings/listings';
import { getSize } from '../utils/iterable/getSize';
import api from '../utils/api';

import { PUBLISH_ACTION, STATUS } from '../constants/listings';
import { MESSAGE, NOTIFICATION } from '../constants/notifications';
import { SYNC_INDICATOR } from '../constants/shops';
import { EMPTY, VELA } from '../constants';
import { OPERATIONS } from '../constants/product';
import { MODALS } from '../constants/modal';

import actions from '../actions';

const listingsController = createController();
const listingIdsController = createController();
const statusesController = createController();

function abort(controller) {
  if (controller.isActive()) {
    controller.stop();
  }
}

function abortAll() {
  [
    listingsController,
    listingIdsController,
    statusesController,
  ].forEach(abort);
}

function* bootstrap(state) {
  abortAll();

  const channel = state.getIn(['schedule', 'channel']);
  const shopId = state.getIn(['schedule', 'shopId']);
  const db = state.getIn(['schedule', 'db']);

  if (!channel || !db || !shopId) {
    return state.deleteIn(['schedule', 'listings']);
  }

  const listings = Map({
    channel,
    db,
    pagination: Map(getPagination()),
    products: EMPTY.MAP,
    shopId,
    updates: EMPTY.MAP,
  });

  yield function effect(dispatch) {
    dispatch(actions.schedule.listings.getListings({ clear: true }));
  };

  return state.setIn(['schedule', 'listings'], listings);
}

function* cleanUp(state) {
  abortAll();

  return state.deleteIn(['schedule', 'listings']);
}

function* getListings(state, { clear, more } = {}) {
  if (!state.hasIn(['schedule', 'listings', 'shopId'])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    const { signal } = listingsController.start();
    const listings = state.getIn(['schedule', 'listings']);
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const pagination = listings.get('pagination');
    const limit = pagination.get('limit');
    const offset = clear
      ? 0
      : more
        ? pagination.get('offset') + limit
        : pagination.get('offset');

    api.schedule.listings
      .get({ db, params: { limit, offset, type: 'publish' }, shopId, signal })
      .then(
        (response) => dispatch(actions.schedule.listings.getListings.fulfilled({ limit, offset, response, shopId })),
        (error) => dispatch(actions.schedule.listings.getListings.rejected({ error, signal, shopId })),
      );
  };

  return state.getIn(['schedule', 'listings', 'loading'])
    ? state
    : state.setIn(['schedule', 'listings', 'loading'], true);
}

function* getListingsFulfilled(state, { limit, offset, response, shopId }) {
  if (shopId !== state.getIn(['schedule', 'listings', 'shopId'])) return state;

  abort(listingsController);

  let total = shapeNumber(response.total);
  let listings = state.getIn(['schedule', 'listings']).delete('loading');

  if (offset === 0 || total === 0) {
    listings = listings
      .set('dates', EMPTY.MAP)
      .set('products', EMPTY.MAP)
      .set('ranges', EMPTY.MAP)
      .set('updates', Map({ total }))
      .setIn(['pagination', 'offset'], 0);

    if (total === 0) {
      const today = getToday();
      const key = getDateKey(today);

      listings = listings
        .setIn(['updates', 'loaded'], 0)
        .set('ranges', Map({ keys: List([key]), timestamps: Map({ [key]: today }) }))
        .set('time', Map( { today, start: today, end: getTimeRangeEnd(today) }));

      return state.setIn(['schedule', 'listings'], listings);
    }
  } else if (getSize(response.scheduled_updates) === 0 && offset > 0) {
    total = listings.getIn(['updates', 'loaded'], 0);

    listings = listings
      .setIn(['pagination', 'offset'], Math.floor(Math.max(0, total - 1) / limit) * limit)
      .setIn(['updates', 'total'], total);

    return state.setIn(['schedule', 'listings'], listings);
  } else {
    if (offset !== listings.getIn(['pagination', 'offset'])) {
      listings = listings.setIn(['pagination', 'offset'], offset);
    }

    if (total !== listings.getIn(['updates', 'total'])) {
      listings = listings.setIn(['updates', 'total'], total);
    }
  }

  const userShops = state.get('shops');

  listings = shapeScheduledListingsForApp({ response, state: listings, userShops });

  if (total > 0 && listings.getIn(['updates', 'loaded']) === listings.getIn(['updates', 'total'])) {
    const lastRange = listings.getIn(['ranges', 'timestamps', listings.getIn(['ranges', 'keys']).last()]);

    if (isAfter(listings.getIn(['time', 'start']), lastRange)) {
      listings = listings
        .setIn(['time', 'start'], lastRange)
        .setIn(['time', 'end'], getTimeRangeEnd(lastRange));
    }
  }

  return state.setIn(['schedule', 'listings'], listings);
}

function* getListingsRejected(state, { error, signal, shopId }) {
  if (signal.aborted || shopId !== state.getIn(['schedule', 'listings', 'shopId'])) return state;

  abort(listingsController);

  yield function effect(dispatch) {
    console.error(error);
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.LOAD_LISTINGS,
      })
    );
  };

  return state.deleteIn(['schedule', 'listings', 'loading']);
}

function* publish(state, { products }) {
  yield function effect(dispatch) {
    const listings = state.getIn(['schedule', 'listings']);
    const db = listings.get('db');
    const shopId = listings.get('shopId');
    const channel = listings.get('channel');
    const publishStatus = getPublishStatus({ action: PUBLISH_ACTION.PUBLISH, channel });
    const operations = [
      { products: products.toArray(), type: OPERATIONS[channel].STATUS, value: publishStatus },
      { products: products.toArray(), type: OPERATIONS[channel].SCHEDULED_AT, value: '' },
    ];

    api.products
      .bulkEditProducts({ channel, db, operations, shopId, status: publishStatus })
      .then(
        () => dispatch(actions.schedule.listings.publish.fulfilled({ products, shopId })),
        (error) => dispatch(actions.schedule.listings.publish.rejected({ error, products, shopId })),
      );
  };

  function updateProcessing(processing = EMPTY.SET) {
    return processing.union(products);
  }

  return state.updateIn(['schedule', 'listings', 'products', 'processing'], updateProcessing);
}

function* publishFulfilled(state, { products, shopId }) {
  yield function effect(dispatch) {
    dispatch(actions.listings.toggleState({ listings: products, state: ['processing', shopId] }));

    if (state.getIn(['shops', 'syncData', shopId, 'indicator']) !== SYNC_INDICATOR.PUBLISH) {
      dispatch(
        actions.shops.setData({
          path: ['syncData', shopId],
          value: Map({ indicator: SYNC_INDICATOR.PUBLISH }),
        })
      );
    }

    if (state.getIn(['schedule', 'shopId']) === shopId) {
      dispatch(actions.schedule.getCounts());
    }

    if (state.getIn(['schedule', 'listings', 'shopId']) === shopId) {
      dispatch(actions.schedule.listings.getListings({ clear: true }));
    }
  };

  return state;
}

function* publishRejected(state, { error, products, shopId }) {
  yield function effect(dispatch) {
    if (error) console.error(error);

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: MESSAGE.FAIL.PUBLISH_LISTINGS,
      })
    );
  };

  function updateProducts(scheduledProducts) {
    const processing = scheduledProducts.get('processing', EMPTY.SET).subtract(products);

    return getSize(processing) > 0
      ? scheduledProducts.set('processing', processing)
      : scheduledProducts.delete('processing');
  }

  return (
    state.getIn(['schedule', 'listings', 'shopId']) === shopId &&
    getSize(products.intersect(state.getIn(['schedule', 'listings', 'products', 'processing'], EMPTY.SET))) > 0
  )
    ? state.updateIn(['schedule', 'listings', 'products'], updateProducts)
    : state;
}

function* reschedule(state, { products, value }) {
  if (value === undefined) {
    yield function effect(dispatch) {
      const scheduledAt = state.getIn(['schedule', 'listings', 'products', 'byId', products.first(), 'scheduledAt']);

      dispatch(actions.schedule.listings.setModal({ products, scheduledAt, type: MODALS.RESCHEDULE_PUBLISH }));
    };

    return state;
  }

  yield function effect(dispatch) {
    const listings = state.getIn(['schedule', 'listings']);
    const channel = listings.get('channel');
    const shopId = listings.get('shopId');
    const db = listings.get('db');
    const operations = [{ products: products.toArray(), type: OPERATIONS[channel].SCHEDULED_AT, value }];

    dispatch(
      actions.shops.setData({
        path: ['syncData', shopId],
        value: Map({ indicator: SYNC_INDICATOR.HIDDEN }),
      })
    );

    api.products
      .bulkEditProducts({ db, operations, shopId, channel, status: STATUS[VELA].SCHEDULED })
      .then(
        (response) => dispatch(actions.schedule.listings.reschedule.fulfilled({ products, response, shopId, value })),
        (error) => dispatch(actions.schedule.listings.reschedule.rejected({ error, products, shopId, value })),
      );
  };

  function updateProcessing(processing = EMPTY.SET) {
    return processing.union(products);
  }

  return state.updateIn(['schedule', 'listings', 'products', 'processing'], updateProcessing);
}

function* rescheduleFulfilled(state, { products, response, shopId, value }) {
  if (response?.result !== 'succeeded') {
    yield function effect(dispatch) {
      dispatch(actions.schedule.listings.reschedule.rejected({ error: response?.error, products, shopId, value }));
    };

    return state;
  }

  yield function effect(dispatch) {
    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.SUCCESS,
        message: value
          ? MESSAGE.SUCCESS.RESCHEDULE_PUBLICATION
          : MESSAGE.SUCCESS.CANCEL_PUBLICATION,
      })
    );
  };

  function updateSelected(selected = EMPTY.ORDERED_SET) {
    return selected.subtract(products);
  }

  return value && state.getIn(['schedule', 'listings', 'shopId']) === shopId
    ? state
      .setIn(['schedule', 'listings', 'scroll'], getDateKey(value))
      .updateIn(['schedule', 'listings', 'products', 'selected'], updateSelected)
    : state;
}

function* rescheduleRejected(state, { error, products, shopId, value }) {
  yield function effect(dispatch) {
    if (error) console.error(error);

    if (state.getIn(['shops', 'syncData', shopId, 'indicator']) === SYNC_INDICATOR.HIDDEN) {
      dispatch(actions.shops.setData({ path: ['syncData', shopId] }));
    }

    dispatch(
      actions.notifications.add({
        type: NOTIFICATION.ERROR,
        message: value
          ? MESSAGE.FAIL.RESCHEDULE_PUBLICATION
          : MESSAGE.FAIL.CANCEL_PUBLICATION,
      })
    );
  };

  function updateProducts(scheduledProducts) {
    const processing = scheduledProducts.get('processing', EMPTY.SET).subtract(products);

    return getSize(processing) > 0
      ? scheduledProducts.set('processing', processing)
      : scheduledProducts.delete('processing');
  }

  return (
    state.getIn(['schedule', 'listings', 'shopId']) === shopId &&
    getSize(products.intersect(state.getIn(['schedule', 'listings', 'products', 'processing'], EMPTY.SET))) > 0
  )
    ? state.updateIn(['schedule', 'listings', 'products'], updateProducts)
    : state;
}

function* setData(state, { path, value }) {
  const fullPath = Array.isArray(path)
    ? ['schedule', 'listings', ...path]
    : ['schedule', 'listings', path];

  if (value !== undefined) {
    return state.setIn(fullPath, value);
  }

  let result = state.deleteIn(fullPath);

  fullPath.pop();

  while (fullPath.length > 1) {
    if (!getSize(result.getIn(fullPath))) {
      result = result.deleteIn(fullPath);
    }

    fullPath.pop();
  }

  return result;
}

function* setModal(state, payload) {
  return payload
    ? state.setIn(['schedule', 'listings', 'modal'], Map(payload))
    : state.deleteIn(['schedule', 'listings', 'modal']);
}

function* setTimeRange(state, { start }) {
  return state
    .setIn(['schedule', 'listings', 'time', 'start'], start)
    .setIn(['schedule', 'listings', 'time', 'end'], getTimeRangeEnd(start));
}

function* toggleProduct(state, { productId }) {
  function updateSelected(selected = EMPTY.ORDERED_SET) {
    return selected.has(productId)
      ? selected.delete(productId)
      : selected.add(productId);
  }

  return state.updateIn(['schedule', 'listings', 'products', 'selected'], updateSelected);
}

const scheduleListings = {
  [actions.schedule.listings.bootstrap]: bootstrap,
  [actions.schedule.listings.cleanUp]: cleanUp,
  [actions.schedule.listings.getListings]: getListings,
  [actions.schedule.listings.getListings.fulfilled]: getListingsFulfilled,
  [actions.schedule.listings.getListings.rejected]: getListingsRejected,
  [actions.schedule.listings.publish]: publish,
  [actions.schedule.listings.publish.fulfilled]: publishFulfilled,
  [actions.schedule.listings.publish.rejected]: publishRejected,
  [actions.schedule.listings.reschedule]: reschedule,
  [actions.schedule.listings.reschedule.fulfilled]: rescheduleFulfilled,
  [actions.schedule.listings.reschedule.rejected]: rescheduleRejected,
  [actions.schedule.listings.setData]: setData,
  [actions.schedule.listings.setModal]: setModal,
  [actions.schedule.listings.setTimeRange]: setTimeRange,
  [actions.schedule.listings.toggleProduct]: toggleProduct,
};

export default scheduleListings;
